import {
  CalendarDays,
  CircleDashed,
  CircleX,
  Lock,
  Plug,
  Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { ROUTES } from "@/constants/routes";
import CampaignIntegrationStatusBadge from "@/features/campaigns/jobboards/components/CampaignIntegrationStatusBadge";
import {
  getPercentSpent,
  getSpendWindow,
} from "@/features/campaigns/jobboards/utils/getSpendProgress";
import {
  useReportingData,
  useReportingIntegrations,
} from "@/features/integrations/modules/reporting/api";
import { translateIntegrationDisplayName } from "@/features/integrations/utils";
import { useAuth } from "@/lib/hooks/useAuth";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { useValueFormatter } from "@/utils/useValueFormatter";

type CampaignJobBoardCardProps = {
  jobBoard: CampaignJobBoardType;
  campaignId: string;
  clientId: string;
};

function formatDate(value?: string | null, locale?: string) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale ?? undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function CampaignJobBoardCard({
  jobBoard,
  campaignId,
  clientId,
}: CampaignJobBoardCardProps) {
  const { t, i18n } = useTranslation();
  const { format } = useValueFormatter();
  const navigate = useNavigate();
  const { tenantId, context } = useAuth();

  const viewUrl = `./${ROUTES.campaignIntegrations}/${jobBoard.clientIntegrationModuleId}`;
  const integrationName = jobBoard.integrationName;

  const { data: reportingData, isLoading: isReportingLoading } =
    useReportingIntegrations({
      tenantId,
      clientId,
    });
  const reportingEnabledSlugs = reportingData?.integrations ?? [];
  const hasReportingEnabled =
    !!integrationName && reportingEnabledSlugs.includes(integrationName);

  const { data: reportingCapableData, isLoading: isReportingCapableLoading } =
    useReportingIntegrations(
      { tenantId, clientId, isActive: false },
      { enabled: !isReportingLoading && !hasReportingEnabled }
    );
  const reportingCapableSlugs = reportingCapableData?.integrations ?? [];
  const canEnableReporting =
    !!integrationName && reportingCapableSlugs.includes(integrationName);

  const isAdmin = context === "admin";
  const integrationDetailPath = `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`;

  const { startDate, endDate, hasElapsedDays } = getSpendWindow(new Date());

  const {
    data: integrationSpend,
    isLoading: isSpendLoading,
    isError: isSpendError,
  } = useReportingData({
    endpoint: "aggregated",
    level: "integrations",
    params: {
      campaignIds: [campaignId],
      startDate,
      endDate,
      tenantId: tenantId ?? undefined,
      clientId,
      pageSize: reportingEnabledSlugs.length,
    },
    enabled: hasElapsedDays && reportingEnabledSlugs.length > 0,
  });

  const integrationSlug = integrationName?.toLowerCase();
  const spend =
    integrationSpend?.data.find(
      (row) => row.integrationName.toLowerCase() === integrationSlug
    )?.cost ?? 0;
  const budget = jobBoard.currentMonthBudget ?? jobBoard.monthlyBudget;
  const percentSpent = getPercentSpent(spend, budget);

  const throughDate = new Intl.DateTimeFormat(i18n.language, {
    month: "short",
    day: "numeric",
  }).format(endDate);

  const isSpendSectionLoading =
    isReportingLoading ||
    (hasReportingEnabled
      ? hasElapsedDays && isSpendLoading
      : isReportingCapableLoading);

  function renderSpendSection() {
    if (isSpendSectionLoading) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-14" />
            <Skeleton className="h-4 w-28" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
          <Skeleton className="h-3 w-44" />
        </div>
      );
    }

    if (!hasReportingEnabled) {
      if (canEnableReporting) {
        return (
          <EmptyState
            layout="inline"
            icon={Plug}
            iconClassName="text-primary"
            title={t(
              "campaigns.jobBoards.spend.notConnectedTitle",
              "Spend reporting not connected"
            )}
            description={t(
              "campaigns.jobBoards.spend.notConnectedDescription",
              "Enable spend tracking for this jobboard."
            )}
            action={
              isAdmin
                ? {
                  href: integrationDetailPath,
                  onClick: (e) => e.stopPropagation(),
                  label: t(
                    "campaigns.jobBoards.spend.enableReportingModule",
                    "Enable reporting module"
                  ),
                }
                : undefined
            }
          />
        );
      }

      return (
        <EmptyState
          layout="inline"
          icon={Lock}
          title={t(
            "campaigns.jobBoards.spend.unavailableTitle",
            "Spend reporting unavailable"
          )}
          description={t(
            "campaigns.jobBoards.spend.unavailableDescription",
            "This jobboard doesn't provide spend data, so budget tracking can't be shown."
          )}
        />
      );
    }

    if (!hasElapsedDays) {
      return (
        <EmptyState
          layout="inline"
          icon={CircleDashed}
          title={t(
            "campaigns.jobBoards.spend.noDataYetTitle",
            "No spend data yet this month"
          )}
          description={t(
            "campaigns.jobBoards.spend.noDataYetDescription",
            "Spend is reported a day behind, so this month's figures appear from tomorrow."
          )}
        />
      );
    }

    if (isSpendError) {
      return (
        <EmptyState
          layout="inline"
          icon={CircleX}
          title={t(
            "campaigns.jobBoards.spend.loadFailedTitle",
            "Spend data unavailable"
          )}
          description={t(
            "campaigns.jobBoards.spend.loadFailedDescription",
            "This month's spend couldn't be loaded. Reload the page to try again."
          )}
        />
      );
    }

    if (percentSpent === null) {
      return (
        <EmptyState
          layout="inline"
          icon={Wallet}
          title={t(
            "campaigns.jobBoards.spend.noBudgetTitle",
            "No monthly budget set"
          )}
          description={t("campaigns.jobBoards.spend.noBudgetDescription", {
            defaultValue:
              "{{spent}} spent this month. Set a budget to track it against a limit.",
            spent: format(spend, "currency"),
          })}
        />
      );
    }

    const roundedPercent = Math.round(percentSpent);

    return (
      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-lg font-semibold cursor-default">
              {roundedPercent}%
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("campaigns.jobBoards.spend.throughDate", {
              defaultValue: "Through {{date}}",
              date: throughDate,
            })}
          </TooltipContent>
        </Tooltip>
        <div
          role="progressbar"
          aria-valuenow={roundedPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t("campaigns.jobBoards.spend.progressLabel", {
            defaultValue: "{{percent}}% of this month's budget spent",
            percent: roundedPercent,
          })}
          className="h-2 w-full rounded-full bg-muted"
        >
          <div
            className="h-2 rounded-full bg-primary transition-all"
            style={{ width: `${Math.min(roundedPercent, 100)}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t("campaigns.jobBoards.spend.spentThisMonth", {
            defaultValue: "{{spent}} of {{budget}} spent this month",
            spent: format(spend, "currency"),
            budget: format(budget, "currency"),
          })}
        </p>
      </div>
    );
  }

  return (
    <Card
      hover
      className="cursor-pointer"
      onClick={() => navigate(viewUrl)}
    >
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <Link
            to={viewUrl}
            onClick={(e) => e.stopPropagation()}
            className="hover:underline underline-offset-2"
          >
            {translateIntegrationDisplayName(
              jobBoard.integrationDisplayName ?? jobBoard.jobBoardName,
              t,
              i18n
            )}
          </Link>
          <CampaignIntegrationStatusBadge campaignIntegration={jobBoard} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarDays className="size-4 shrink-0" />
          <span>{formatDate(jobBoard.startDate, i18n.language)}</span>
          <span>→</span>
          <span>
            {jobBoard.endDate
              ? formatDate(jobBoard.endDate, i18n.language)
              : t("common.ongoing", "Ongoing")}
          </span>
        </div>

        <div className="mt-4">{renderSpendSection()}</div>
      </CardContent>
    </Card>
  );
}

export default CampaignJobBoardCard;
